import { ChangeEvent, Suspense, useCallback, useEffect } from 'react';
import { Grid, TextField } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import { useOrganisationManagementServiceGetOrganisation } from '@/api/management/queries';
import { OrganisationManagementService, SafetyPredictionTrigger, ThresholdsByTrigger } from '@/api/management/requests';
import { TugOperationsConfiguration, saveTugOperationsConfiguration } from '@/api/management/tugOperationsService';
import CreateForm from '@/components/management/CreateForm/CreateForm';
import ThresholdsEditableTable from '@/features/Admin/OrganisationManagement/ConfigureGraph/ThresholdsEditableTable';
import TugOperationsConfigSection from '@/features/Admin/OrganisationManagement/ConfigureGraph/TugOperationsConfigSection';

export const thresholdsConfigurationAtom = atom<ThresholdsByTrigger[]>([]);
export const tugOperationsConfigurationAtom = atom<TugOperationsConfiguration[]>([]);

const defaultPeriodAtom = atom<number>(3);

function ConfigureGraphForOrganisation() {
  const { organisationId } = useParams();
  const [thresholdsByTriggers, setThresholdsByTriggers] = useAtom(thresholdsConfigurationAtom);
  const [defaultPeriod, setDefaultPeriod] = useAtom(defaultPeriodAtom);
  const [tugRows] = useAtom(tugOperationsConfigurationAtom);

  const {
    data: organisation,
    isLoading,
    isSuccess,
  } = useOrganisationManagementServiceGetOrganisation({ organisationId }, [organisationId]);

  useEffect(() => {
    if (isSuccess) {
      setThresholdsByTriggers(organisation?.graphConfiguration?.thresholdsByTriggers);
      setDefaultPeriod(organisation?.graphConfiguration?.defaultPeriod);
    }
  }, [isSuccess, setDefaultPeriod, setThresholdsByTriggers, organisation?.graphConfiguration]);

  const mutation = useMutation({
    mutationFn: async (values: {
      organisationId?: string;
      requestBody: { defaultPeriod: number; thresholdsByTriggers: ThresholdsByTrigger[] };
      tugRows: TugOperationsConfiguration[];
    }) => {
      if (!values.organisationId) return;

      await OrganisationManagementService.updateOrganisationGraphConfiguration(values.organisationId, values.requestBody);

      if (values.tugRows.length > 0) {
        await saveTugOperationsConfiguration(values.organisationId, values.tugRows);
      }
    },
  });

  const getFormValues = useCallback(() => {
    return {
      organisationId: organisation?.organisationId,
      requestBody: {
        defaultPeriod,
        thresholdsByTriggers,
      },
      tugRows,
    };
  }, [defaultPeriod, thresholdsByTriggers, organisation, tugRows]);

  const handlePeriodChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDefaultPeriod(parseInt(e.target.value));
    },
    [setDefaultPeriod]
  );

  if (isLoading) {
    return;
  }

  return (
    <Suspense>
      <CreateForm
        title={`Configure Graph for ${organisation?.name}`}
        backUri="/admin/organisations"
        getFormValues={getFormValues}
        mutation={mutation}
      >
        <Grid container spacing={2} sx={{ pl: 5 }}>
          <Grid item xs={12}>
            <TextField
              label={'Graph default period (in days)'}
              type="number"
              value={defaultPeriod}
              onChange={handlePeriodChange}
              required={true}
            />
          </Grid>
          {Object.values(SafetyPredictionTrigger).map((trigger) => (
            <Grid key={`threshold-table-${trigger}`} item xs={12}>
              <ThresholdsEditableTable trigger={trigger} />
            </Grid>
          ))}
          {organisationId && (
            <Grid item xs={12}>
              <TugOperationsConfigSection organisationId={organisationId} />
            </Grid>
          )}
        </Grid>
      </CreateForm>
    </Suspense>
  );
}

export default ConfigureGraphForOrganisation;
